import { theme } from '@Guidelines/Theme/theme';
import useAuthenticatedUser from '@UI/Providers/AuthenticatedUserProvider/useAuthenticatedUser';
import { css } from '@emotion/react';
import type * as React from 'react';
import resolvePostLoginDestination from '@UI/Routing/resolvePostLoginDestination';
import { Navigate, useLocation } from 'react-router-dom';
import { Spinner } from '@verclam/ui-kit';

const resolvingStyle = css`
	display: flex;
	align-items: center;
	justify-content: center;
	min-height: 100vh;
	background-color: ${theme.colors.surfaceInverted};
`;

type Props = {
	readonly children: React.ReactNode;
};

/**
 * The only one of the three route guards a signed-out visitor actually reaches, so it is the only
 * one that cannot render `null` while `sessionStatus` is `'unknown'`: doing so would leave the
 * product's entry door blank for the length of a `GET /api/me` round trip. It cannot render the
 * form either — that would flash the login screen at an already authenticated visitor right before
 * ejecting them.
 */
const RequireAnonymous: React.FC<Props> = ({ children }) => {
	const { sessionStatus } = useAuthenticatedUser();
	const location = useLocation();

	if (sessionStatus === 'unknown') {
		return (
			<div css={resolvingStyle}>
				<Spinner aria-label="Résolution de la session" />
			</div>
		);
	}

	if (sessionStatus === 'authenticated') {
		return <Navigate to={resolvePostLoginDestination(location.state)} replace />;
	}

	return children;
};

export default RequireAnonymous;
